import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { UniversalHeader, BottomNavigation, NavItem } from '../../../shared/ui';
import ObjectsList from './ObjectsList';
import ObjectPage from './ObjectPage';
import SiteProfilePage from './SiteProfilePage';
import RyaskovProCard from './RyaskovProCard';
import StatisticsCard from './StatisticsCard';
import { HabiBotPage } from '../../habiBot';
import { ProfilePage } from '../../profile';
import { SiteV2, sitesV2Api } from '../../../api/sitesV2Api';
import { useOptimizedSignalR, SessionEvent, QuestionStatusChangedEvent } from '../../../hooks/useOptimizedSignalR';
import { useToast } from '../../../shared/contexts/ToastContext';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useBeta } from '../../../shared/contexts/BetaContext';
import { FULLSCREEN_CARD_OPENED_EVENT, FULLSCREEN_CARD_CLOSED_EVENT } from '../../../shared/ui/cards/FullscreenCardModal';
import styles from './DashboardScreenNew.module.css';
import './DashboardScreenNew.optimization.css';

const DashboardScreenNew: React.FC = () => {
  const location = useLocation();
  const { isBeta } = useBeta();
  const [selectedObjectId, setSelectedObjectId] = useState<number | null>(null); // рабочая страница объекта (для участников)
  const [siteProfileId, setSiteProfileId] = useState<number | null>(null); // социальный профиль объекта (из ленты)
  const [viewedProfileUserId, setViewedProfileUserId] = useState<number | null>(null); // профиль автора из ленты
  const [userSites, setUserSites] = useState<SiteV2[]>([]);
  const [activeTab, setActiveTab] = useState<string>('projects');
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false); // Отслеживаем открытие fullscreen карточки
  // Состояние для кнопок ленты (избранное, создать пост) — кнопки в главной шапке
  const [showFeedFavorites, setShowFeedFavorites] = useState(false);
  const [showFeedCreatePost, setShowFeedCreatePost] = useState(false);
  const [showHabiCreateModal, setShowHabiCreateModal] = useState(false);

  // Проверяем state из навигации для установки активной вкладки
  useEffect(() => {
    const tabFromState = (location.state as any)?.tab;
    if (tabFromState && ['ryaskovpro', 'projects', 'statistics', 'habibot', 'profile'].includes(tabFromState)) {
      setActiveTab(tabFromState);
      // Очищаем state после использования
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const { showInfo, showSuccess } = useToast();
  const { user } = useAuth();
  const [isResizing, setIsResizing] = useState(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Оптимизация: предотвращаем перестроения при изменении размера экрана
  useEffect(() => {
    const handleResize = () => {
      // Добавляем класс resizing для блокировки transitions
      setIsResizing(true);
      
      // Очищаем предыдущий таймаут
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      // Убираем класс resizing через небольшую задержку после окончания resize
      resizeTimeoutRef.current = setTimeout(() => {
        setIsResizing(false);
      }, 150);
    };

    // Подписываемся на событие resize только для блокировки transitions
    // НЕ вызываем перерисовку компонентов
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Отслеживаем открытие/закрытие fullscreen карточки для скрытия ObjectPage
  useEffect(() => {
    const onFullscreenOpened = () => {
      setIsFullscreenOpen(true);
    };
    
    const onFullscreenClosed = () => {
      setIsFullscreenOpen(false);
    };
    
    window.addEventListener(FULLSCREEN_CARD_OPENED_EVENT, onFullscreenOpened);
    window.addEventListener(FULLSCREEN_CARD_CLOSED_EVENT, onFullscreenClosed);
    
    return () => {
      window.removeEventListener(FULLSCREEN_CARD_OPENED_EVENT, onFullscreenOpened);
      window.removeEventListener(FULLSCREEN_CARD_CLOSED_EVENT, onFullscreenClosed);
    };
  }, []);

  // Загружаем список объектов пользователя из кеша или при переходе на вкладку проектов
  useEffect(() => {
    const loadUserSites = async () => {
      try {
        // Сначала проверяем кеш
        const cachedSites = sitesV2Api.getCachedMySites();
        if (cachedSites) {
          // Удаляем дубликаты по id для предотвращения ошибок с ключами
          const uniqueSites = cachedSites.filter((site, index, self) => 
            index === self.findIndex(s => s.id === site.id)
          );
          setUserSites(uniqueSites);
        }
        // Если кеша нет, загружаем только при переходе на вкладку проектов
        // Это будет сделано в ObjectsList
      } catch (error) {
        console.error('Ошибка загрузки объектов пользователя:', error);
      }
    };
    loadUserSites();
  }, []);

  const handleProjectSelect = (project: SiteV2) => {
    setSelectedObjectId(project.id);
  };

  const handleBackToList = () => {
    setSelectedObjectId(null);
  };

  // Клик по объекту в ленте → социальный профиль объекта (SiteProfilePage)
  const handleFeedSiteClick = useCallback((siteId: number) => {
    setSiteProfileId(siteId);
  }, []);

  const handleCloseSiteProfile = useCallback(() => {
    setSiteProfileId(null);
  }, []);

  // Переход из профиля объекта → рабочее пространство (только для участников)
  const handleOpenWorkspace = useCallback((siteId: number) => {
    setSiteProfileId(null);
    setActiveTab('projects');
    setSelectedObjectId(siteId);
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    // Повторное нажатие «Проекты» на экране объекта — назад к списку
    if (tab === 'projects' && activeTab === 'projects' && selectedObjectId !== null) {
      setSelectedObjectId(null);
      return;
    }

    setActiveTab(tab);

    // Если переключаемся на другую вкладку и открыт объект, закрываем его
    if (selectedObjectId !== null && tab !== 'projects') {
      setSelectedObjectId(null);
    }
  }, [activeTab, selectedObjectId]);

  const BETA_TABS = new Set(['statistics', 'habibot']);

  const navItems: NavItem[] = [
    {
      id: 'ryaskovpro',
      label: 'Лента',
      icon: 'ri-home-line',
      path: '/dashboard-new',
      dashboardTab: 'ryaskovpro',
      onClick: () => handleTabChange('ryaskovpro')
    },
    {
      id: 'projects',
      label: 'Проекты',
      icon: 'ri-building-line',
      path: '/dashboard-new',
      dashboardTab: 'projects',
      onClick: () => handleTabChange('projects')
    },
    {
      id: 'statistics',
      label: 'Обзор',
      icon: 'ri-money-dollar-circle-line',
      path: '/dashboard-new',
      dashboardTab: 'statistics',
      onClick: () => handleTabChange('statistics')
    },
    {
      id: 'habibot',
      label: 'HabiBot',
      icon: 'ri-checkbox-circle-line',
      path: '/dashboard-new',
      dashboardTab: 'habibot',
      onClick: () => handleTabChange('habibot')
    },
    {
      id: 'profile',
      label: 'Профиль',
      icon: 'ri-user-line',
      path: '/dashboard',
      dashboardTab: 'profile',
      onClick: () => handleTabChange('profile')
    }
  ].filter(item => !BETA_TABS.has(item.id) || isBeta);

  // Мемоизированные глобальные обработчики уведомлений для каждого объекта
  // Используем useMemo для создания стабильной Map handlers по siteId
  const globalHandlersMap = React.useMemo(() => {
    const handlersMap = new Map<number, any>();
    
    userSites.forEach(site => {
      handlersMap.set(site.id, {
        // Глобальное уведомление при создании смены - "Вася вышел на работу"
        onSessionCreated: (session: SessionEvent) => {
          if (session.siteId === site.id && session.userId !== user?.id && !session.timeEnd) {
            showInfo(`${session.userName || 'Кто-то'} вышел на работу (${site.name})`, 5000);
          }
        },
        
        // Глобальное уведомление при закрытии смены
        onSessionClosed: (session: SessionEvent) => {
          if (session.siteId === site.id && session.userId !== user?.id) {
            showInfo(`${session.userName || 'Кто-то'} завершил смену (${site.name})`, 4000);
          }
        },
        
        // Глобальное уведомление при создании вопроса
        onQuestionCreated: (question: QuestionStatusChangedEvent) => {
          if (question.site?.id === site.id) {
            showInfo(`Новая дискуссия: ${question.text?.substring(0, 50)}... (${site.name})`, 5000);
          }
        },
        
        // Глобальное уведомление при изменении статуса вопроса (например, решен)
        onQuestionStatusChanged: (question: QuestionStatusChangedEvent) => {
          if (question.site?.id === site.id && question.closed) {
            showSuccess(`Дискуссия закрыта (${site.name})`, 4000);
          }
        },
      });
    });
    
    return handlersMap;
  }, [userSites, user?.id, showInfo, showSuccess]);

  // Определяем содержимое в зависимости от активной вкладки (профиль показывается отдельным окном)
  const renderContent = () => {
    switch (activeTab) {
      case 'ryaskovpro':
        return (
          <RyaskovProCard
            showFavorites={showFeedFavorites}
            showCreatePost={showFeedCreatePost}
            onCloseFavorites={() => setShowFeedFavorites(false)}
            onCloseCreatePost={() => setShowFeedCreatePost(false)}
            onSiteClick={handleFeedSiteClick}
            onUserClick={handleFeedUserClick}
          />
        );
      case 'projects':
        return <ObjectsList onProjectSelect={handleProjectSelect} />;
      case 'statistics':
        return <StatisticsCard />;
      case 'habibot':
        return (
          <HabiBotPage
            embedded
            showCreateModal={showHabiCreateModal}
            onOpenCreateModal={() => setShowHabiCreateModal(true)}
            onCloseCreateModal={() => setShowHabiCreateModal(false)}
            onHabitCreated={() => setShowHabiCreateModal(false)}
          />
        );
      case 'profile':
        return null; // контент профиля — в окне поверх
      default:
        return <ObjectsList onProjectSelect={handleProjectSelect} />;
    }
  };

  const handleBackFromProfile = useCallback(() => {
    setActiveTab('ryaskovpro');
  }, []);

  const handleFeedUserClick = useCallback((userId: number) => {
    if (userId === user?.id) {
      // Свой профиль — просто переключаем на вкладку профиля
      setActiveTab('profile');
    } else {
      // Чужой профиль — открываем как оверлей
      setViewedProfileUserId(userId);
    }
  }, [user?.id]);

  const handleCloseViewedProfile = useCallback(() => {
    setViewedProfileUserId(null);
  }, []);

  // Список всегда в DOM; при открытом объекте или профиле скрыт и не рендерится браузером
  const objectIsOpen = !!selectedObjectId;
  const profileIsOpen = activeTab === 'profile';
  const siteProfileIsOpen = !!siteProfileId;
  const viewedProfileIsOpen = !!viewedProfileUserId;
  const overlayIsOpen = objectIsOpen || profileIsOpen || siteProfileIsOpen || viewedProfileIsOpen;
  return (
    <div 
      ref={dashboardRef}
      className={`dashboard-optimized ${isResizing ? 'resizing' : ''}`}
    >
      {/* Список/контент — всегда в DOM, при открытом объекте или профиле скрыт и не рендерится браузером */}
      <div
        className={styles.dashboardWrapper}
        style={{
          position: overlayIsOpen ? 'absolute' : undefined,
          inset: overlayIsOpen ? 0 : undefined,
          visibility: overlayIsOpen ? 'hidden' : 'visible',
          zIndex: overlayIsOpen ? -1 : 0,
          // Браузер не тратит ресурсы на отрисовку скрытого контента
          contentVisibility: overlayIsOpen ? 'hidden' : 'visible',
          pointerEvents: overlayIsOpen ? 'none' : 'auto',
        }}
      >
        <div className={styles.dashboard}>
          <div className={styles.projectsContainer}>
            <UniversalHeader
              title="Ryaskov.pro"
              className={`dashboard-header${activeTab === 'projects' || activeTab === 'statistics' || activeTab === 'habibot' ? ' dashboard-header--centered' : ''}`}
              headerActions={activeTab === 'ryaskovpro' ? (
                <>
                  <button type="button" title="Поиск" aria-label="Поиск">
                    <i className="ri-search-line" aria-hidden />
                  </button>
                  <button
                    type="button"
                    title="Избранное"
                    aria-label="Избранное"
                    onClick={() => setShowFeedFavorites(true)}
                  >
                    <i className="ri-heart-line" aria-hidden />
                  </button>
                  <button
                    type="button"
                    title="Создать пост"
                    aria-label="Создать пост"
                    onClick={() => setShowFeedCreatePost(true)}
                  >
                    <i className="ri-add-box-line" aria-hidden />
                  </button>
                </>
              ) : activeTab === 'habibot' ? (
                <button
                  type="button"
                  title="Добавить дело"
                  aria-label="Добавить дело"
                  onClick={() => setShowHabiCreateModal(true)}
                >
                  <i className="ri-add-line" aria-hidden />
                </button>
              ) : undefined}
            />
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Профиль объекта из ленты — лёгкая визитка: лента постов + команда */}
      {siteProfileId && (
        <div
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 2,
            height: '100vh',
            width: '100%',
            maxWidth: '1200px',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch' as any,
          }}
        >
          <SiteProfilePage
            siteId={siteProfileId}
            onBack={handleCloseSiteProfile}
            onOpenWorkspace={handleOpenWorkspace}
            onUserClick={(userId) => {
              setSiteProfileId(null);
              setViewedProfileUserId(userId);
            }}
          />
        </div>
      )}

      {/* Профиль автора из ленты — полноэкранный оверлей */}
      {viewedProfileIsOpen && (
        <div
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 3,
            height: '100vh',
            width: '100%',
            maxWidth: '1200px',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch' as any,
          }}
        >
          <ProfilePage
            hideHeader={true}
            hideBottomNavigation
            userId={viewedProfileUserId ?? undefined}
            onBack={handleCloseViewedProfile}
          />
        </div>
      )}

      {/* Окно объекта поверх списка; скрывается при открытой fullscreen карточке */}
      {selectedObjectId && (
        <div 
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            zIndex: isFullscreenOpen ? -1 : 1, 
            height: '100vh', 
            width: '100%', 
            maxWidth: '1200px',
            visibility: isFullscreenOpen ? 'hidden' : 'visible',
            opacity: isFullscreenOpen ? 0 : 1,
            pointerEvents: isFullscreenOpen ? 'none' : 'auto',
            contentVisibility: isFullscreenOpen ? 'hidden' : 'visible',
            transition: 'opacity 0.2s ease'
          }}
        >
          <ObjectPage 
            objectId={selectedObjectId} 
            onBack={handleBackToList}
            initialSite={userSites.find(site => site.id === selectedObjectId) || undefined}
          />
        </div>
      )}

      {/* Окно профиля — перестроение контента без перехода на отдельную страницу */}
      {profileIsOpen && (
        <div 
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            zIndex: 1, 
            height: '100vh', 
            width: '100%', 
            maxWidth: '1200px',
            visibility: 'visible',
            pointerEvents: 'auto',
            contentVisibility: 'visible',
            transition: 'opacity 0.2s ease',
            /* Родитель .dashboard-optimized с overflow:hidden — иначе контент профиля обрезается снизу без скролла */
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <ProfilePage 
            hideHeader={true} 
            hideBottomNavigation
            onBack={handleBackFromProfile}
          />
        </div>
      )}
      
      {/* Глобальные подписки остаются активными даже при открытом объекте */}
      {userSites.map(site => (
        <GlobalSiteNotifications
          key={`global-notif-${site.id}`}
          siteId={site.id}
          siteName={site.name}
          handlers={globalHandlersMap.get(site.id) || {}}
        />
      ))}
      {/* Нижнее меню — скрываем когда открыт любой полноэкранный оверлей */}
      {!overlayIsOpen && (
        <BottomNavigation
          items={navItems}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      )}
    </div>
  );
};

// Компонент-обертка для глобальных уведомлений по объекту
interface GlobalSiteNotificationsProps {
  siteId: number;
  siteName: string;
  handlers: {
    onSessionCreated?: (session: SessionEvent) => void;
    onSessionClosed?: (session: SessionEvent) => void;
    onQuestionCreated?: (question: QuestionStatusChangedEvent) => void;
    onQuestionStatusChanged?: (question: QuestionStatusChangedEvent) => void;
  };
}

const GlobalSiteNotifications: React.FC<GlobalSiteNotificationsProps> = React.memo(({ siteId, handlers }) => {
  // Используем ref для стабилизации handlers - обновляем их без размонтирования компонента
  const handlersRef = useRef(handlers);
  
  // Обновляем ref при изменении handlers, но НЕ вызываем перемонтирование
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);
  
  // Создаем стабильные handlers, которые всегда используют актуальные значения из ref
  const stableHandlers = React.useMemo(() => ({
    onSessionCreated: (session: SessionEvent) => handlersRef.current.onSessionCreated?.(session),
    onSessionClosed: (session: SessionEvent) => handlersRef.current.onSessionClosed?.(session),
    onQuestionCreated: (question: QuestionStatusChangedEvent) => handlersRef.current.onQuestionCreated?.(question),
    onQuestionStatusChanged: (question: QuestionStatusChangedEvent) => handlersRef.current.onQuestionStatusChanged?.(question),
  }), []); // Пустой массив зависимостей - создается только один раз!
  
  // Подписываемся на события конкретного объекта для глобальных уведомлений
  // Используем уникальный subscriptionId для глобальных уведомлений, чтобы не конфликтовать
  // с подписками внутри ObjectPage
  useOptimizedSignalR(stableHandlers, { 
    siteId, 
    subscriptionId: `global_notifications_site_${siteId}` 
  });
  
  // Компонент не рендерит ничего, только управляет подпиской
  return null;
}, (prevProps, nextProps) => {
  // React.memo comparison function - компонент НЕ перерендеривается если siteId не изменился
  return prevProps.siteId === nextProps.siteId;
});

export default DashboardScreenNew;
